/**
 * Retries a dynamic import up to `maxRetries` times with exponential backoff.
 *
 * Code-split chunks can fail to load due to network timeouts, corporate
 * proxies/firewalls throttling cdn.fellow.app, ad blockers, or flaky mobile
 * connections. This gives the network a few more chances before surfacing
 * an error to the user.
 *
 * Note: browsers may cache failed import responses (see https://github.com/whatwg/html/issues/6768),
 * so retries won't help when the browser cached the failure itself. In that
 * scenario reloadForStaleChunks() forces a full page reload as a last resort.
 */
export async function retryDynamicImport<T>(
	importFn: () => Promise<T>,
	maxRetries = 3,
	baseDelayMs = 1000,
): Promise<T> {
	for (let attempt = 0; attempt <= maxRetries; attempt++) {
		try {
			return await importFn();
		} catch (error) {
			if (attempt === maxRetries) {
				throw error;
			}
			const delay = baseDelayMs * 2 ** attempt;
			await new Promise(resolve => setTimeout(resolve, delay));
		}
	}
	// Unreachable, but TypeScript needs it
	throw new Error("retryDynamicImport: unexpected state");
}

/**
 * Returns true when the error is a failed dynamic import — typically caused
 * by network timeouts, proxy/firewall blocks, or browser-cached failures.
 * Note: our CDN retains all chunks indefinitely, so this is not a stale-chunk
 * problem; the files exist but the client couldn't reach them.
 */
export function isDeployError(error: unknown): boolean {
	const message = String(error);
	return (
		message.includes("Failed to fetch dynamically imported module") ||
		message.includes("Importing a module script failed")
	);
}

const RELOAD_STORAGE_KEY = "chunk-reload-ts";
// Must outlast a full retry-reload-retry cycle to prevent infinite loops.
// Worst case on slow devices: ~7s retries + ~5s page load + ~7s retries = ~19s.
// 30s gives comfortable margin.
const RELOAD_COOLDOWN_MS = 30_000;

/**
 * Reloads the page once as a last-resort recovery after all import retries
 * have been exhausted.
 *
 * When the browser caches a failed module response, subsequent import() calls
 * return the cached failure instantly — retries alone can't fix this. A full
 * page reload clears the module cache and gives the browser a fresh chance to
 * fetch the chunk.
 *
 * A sessionStorage timestamp prevents infinite reload loops: if we already
 * reloaded within the last 30 seconds, we bail out and let the error surface.
 *
 * @param doReload - override for testing; defaults to window.location.reload().
 * @returns true if a reload was triggered, false if skipped (cooldown active).
 */
export function reloadForStaleChunks(doReload: () => void = () => window.location.reload()): boolean {
	try {
		const lastReload = Number(sessionStorage.getItem(RELOAD_STORAGE_KEY) || "0");
		if (Date.now() - lastReload > RELOAD_COOLDOWN_MS) {
			sessionStorage.setItem(RELOAD_STORAGE_KEY, String(Date.now()));
			doReload();
			return true;
		}
	} catch {
		// sessionStorage may be unavailable (private browsing, storage full)
	}
	return false;
}
